import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import CreatorNav from '@/components/CreatorNav';
import CreatorTopMenuBar from '@/components/CreatorTopMenuBar';
import CreatorMobileBottomNav from '@/components/CreatorMobileBottomNav';
import EditMetadataForm from '@/components/EditMetadataForm';
import OutlineViewerModalV3 from '@/components/OutlineViewerModalV3';
import UploadCompletionModal from '@/components/UploadCompletionModal';
import TTSDecisionModal from '@/components/TTSDecisionModal';
import TTSProgressModal from '@/components/TTSProgressModal';
import StreamlinedUploadFlow from '@/components/StreamlinedUploadFlow';
import { getBookCoverUrl, hasCoverImage } from '@/lib/coverUtils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload, BarChart3, Edit, Eye, TrendingUp, Clock, Award, Plus, ExternalLink, Archive, Trash2, Mail, Loader2, Calendar, Users, MessageSquare, Volume2, DollarSign, BookOpen } from 'lucide-react';
import axios from 'axios';
import { useNotification } from '@/contexts/NotificationContext';
import UniversalFooter from '@/components/UniversalFooter';
import { BACKEND_URL } from '@/config/api';

// BACKEND_URL imported from centralized config
const API = `${BACKEND_URL}/api`;

const CreatorConsole = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { notifyError } = useNotification();
  const bookDetailsAnchorRef = React.useRef(null);
  const [manuscripts, setManuscripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBook, setSelectedBook] = useState(null);
  const [showActivationModal, setShowActivationModal] = useState(false);
  const [activationAction, setActivationAction] = useState(null); // 'activate' or 'deactivate'
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showStreamlinedUpload, setShowStreamlinedUpload] = useState(false);
  const [authorProfile, setAuthorProfile] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showOutlineModalV3, setShowOutlineModalV3] = useState(false);
  const [selectedManuscriptIdV3, setSelectedManuscriptIdV3] = useState(null);
  const [pendingCompletionData, setPendingCompletionData] = useState(null);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showTTSModal, setShowTTSModal] = useState(false);
  const [showTTSProgress, setShowTTSProgress] = useState(false);
  const [ttsProgress, setTTSProgress] = useState({ completed: 0, total: 0, isComplete: false });
  const [isTTSRegeneration, setIsTTSRegeneration] = useState(false);
  
  // Activation progress modal state
  const [showActivationProgress, setShowActivationProgress] = useState(false);
  const [activationProgressStep, setActivationProgressStep] = useState('');
  const [activationProgressSteps, setActivationProgressSteps] = useState([]);
  const [metrics, setMetrics] = useState({
    totalListens: 0,
    totalMinutes: 0,
    totalEUs: 0
  });
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [ttsLoading, setTtsLoading] = useState(false);
  const [isReadingAloud, setIsReadingAloud] = useState(false);
  const audioRef = React.useRef(null);
  
  // NEW: Background TTS processing states
  const [showProcessingStartedModal, setShowProcessingStartedModal] = useState(false);
  const [showProcessingCompletedModal, setShowProcessingCompletedModal] = useState(false);
  const [processingJobData, setProcessingJobData] = useState(null);
  const [completedJobData, setCompletedJobData] = useState(null);
  const pollingIntervalRef = React.useRef(null);
  
  // Book History state
  const [bookHistory, setBookHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    loadData();
  }, []);
  
  // Fetch book history when selectedBook changes
  useEffect(() => {
    if (selectedBook?.id) {
      fetchBookHistory(selectedBook.id);
    } else {
      setBookHistory(null);
    }
  }, [selectedBook?.id]);
  
  // Handle navigation from CreatorHomePage with manuscript selection
  useEffect(() => {
    if (location.state?.selectedManuscriptId && manuscripts.length > 0) {
      const manuscript = manuscripts.find(m => m.id === location.state.selectedManuscriptId);
      if (manuscript) {
        setSelectedBook(manuscript);
        
        // Scroll to Book Details if requested
        if (location.state.scrollToBookDetails && bookDetailsAnchorRef.current) {
          setTimeout(() => {
            bookDetailsAnchorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 300);
        }
      }
      
      // Clear the state after handling
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, manuscripts, navigate, location.pathname]);
  
  // NEW: Poll for processing status updates
  useEffect(() => {
    console.log('[CreatorConsole] useEffect triggered - checking for processing manuscripts');
    console.log('[CreatorConsole] Total manuscripts:', manuscripts.length);
    
    // Check if any manuscripts are processing
    const processingManuscripts = manuscripts.filter(m => {
      console.log(`[CreatorConsole] Manuscript ${m.title}: processing_status = ${m.processing_status}`);
      return m.processing_status === 'processing' || m.processing_status === 'queued';
    });
    
    console.log('[CreatorConsole] Processing manuscripts:', processingManuscripts.length);
    
    if (processingManuscripts.length === 0) {
      console.log('[CreatorConsole] No processing manuscripts - skipping polling setup');
      return; // No polling needed
    }
    
    console.log('[CreatorConsole] Setting up polling interval (2s) for', processingManuscripts.length, 'manuscripts');
    
    // Poll every 2 seconds for processing manuscripts
    const pollInterval = setInterval(async () => {
      console.log('[CreatorConsole] ⏰ POLLING - Checking status...');
      const token = localStorage.getItem('accessToken');
      
      // Get the IDs of manuscripts that should be polled
      const idsToCheck = processingManuscripts.map(m => m.id);
      console.log('[CreatorConsole] Checking manuscripts:', idsToCheck);
      
      // Poll each processing manuscript
      for (const manuscript of processingManuscripts) {
        try {
          console.log(`[CreatorConsole] Polling status for: ${manuscript.title} (${manuscript.id})`);
          const response = await axios.get(
            `${API}/v3/manuscripts/${manuscript.id}/processing-status`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          const status = response.data;
          console.log(`[CreatorConsole] Status response for ${manuscript.title}:`, status);
          
          // Update manuscript in state
          setManuscripts(prev => prev.map(m => {
            if (m.id === manuscript.id) {
              console.log(`[CreatorConsole] Updating ${m.title}: ${m.processing_status} -> ${status.processing_status}`);
              const updated = {
                ...m,
                processing_status: status.processing_status,
                tts_generation: status.tts_generation,
                current_job: status.current_job  // CRITICAL: Store current_job for progress display
              };
              return updated;
            }
            return m;
          }));
          
          // If just completed, show completion modal
          if ((status.processing_status === 'completed' || status.processing_status === 'partial' || status.processing_status === 'failed') && 
              (manuscript.processing_status === 'processing' || manuscript.processing_status === 'queued')) {
            console.log(`[CreatorConsole] ✅ Processing finished for ${manuscript.title} with status: ${status.processing_status}`);
            
            // Show completion modal
            setCompletedJobData({
              manuscriptId: manuscript.id,
              title: manuscript.title,
              ...status.current_job
            });
            setShowProcessingCompletedModal(true);
            
            // Reload full data to get updated costs, etc.
            setTimeout(() => loadData(), 500);
          }
          
        } catch (error) {
          console.error(`[CreatorConsole] Failed to poll status for ${manuscript.id}:`, error);
        }
      }
    }, 2000); // Poll every 2 seconds for responsive progress updates
    
    return () => {
      console.log('[CreatorConsole] Cleaning up polling interval');
      clearInterval(pollInterval);
    };
  }, [manuscripts]);

  // Preview Modal Handlers
  const handleOpenPreview = () => {
    setShowPreviewModal(true);
  };

  const handleClosePreview = () => {
    setShowPreviewModal(false);
    // Stop audio if playing
    if (audioRef.current) {
      audioRef.current.pause();
      setIsReadingAloud(false);
    }
  };

  const handleReadAloud = () => {
    if (!selectedBook) {
      console.error('[TTS] No book selected');
      return;
    }
    
    // Initialize audio element if not exists
    if (!audioRef.current) {
      audioRef.current = new Audio();
    }
    
    // If already playing, pause
    if (isReadingAloud && audioRef.current) {
      audioRef.current.pause();
      setIsReadingAloud(false);
      return;
    }
    
    // Start the async operation
    setTtsLoading(true);
    
    console.log('[TTS] Requesting audio for book:', selectedBook.id);
    
    // Call backend to get or generate TTS
    axios.post(
      `${BACKEND_URL}/api/manuscripts/${selectedBook.id}/tts/description`,
      {},
      {
        responseType: 'blob'
      }
    )
    .then(response => {
      console.log('[TTS] Audio received, creating blob URL');
      
      // Create audio URL from blob
      const audioBlob = new Blob([response.data], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(audioBlob);
      
      if (!audioRef.current) {
        audioRef.current = new Audio();
      }
      
      // Set up event handlers before playing
      audioRef.current.onended = () => {
        console.log('[TTS] Audio ended');
        setIsReadingAloud(false);
        URL.revokeObjectURL(audioUrl);
      };
      
      audioRef.current.onerror = (e) => {
        console.error('[TTS] Audio playback error:', e);
        setIsReadingAloud(false);
        setTtsLoading(false);
        URL.revokeObjectURL(audioUrl);
        notifyError('Failed to play audio');
      };
      
      // Play audio
      audioRef.current.src = audioUrl;
      audioRef.current.play()
        .then(() => {
          console.log('[TTS] Audio playing');
          setIsReadingAloud(true);
          setTtsLoading(false);
        })
        .catch(err => {
          console.error('[TTS] Play failed:', err);
          setTtsLoading(false);
          notifyError('Could not play audio. Please try again.');
        });
    })
    .catch(err => {
      console.error('[TTS] Failed to get audio:', err);
      setTtsLoading(false);
      notifyError(err.response?.data?.detail || 'Failed to generate audio');
    });
  };

  const getSummary = (book) => {
    const description = book.summary || book.short_description || book.description || '';
    if (!description) return 'No summary available yet for this title.';
    return description;
  };

  const loadData = async () => {
    try {
      console.log('[CreatorConsole] loadData() called at', new Date().toISOString());
      const token = localStorage.getItem('accessToken');
      if (!token) {
        console.error('No auth token found');
        setLoading(false);
        return;
      }

      const config = {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        params: { _t: Date.now() } // Cache-busting
      };

      // Load creator's manuscripts
      console.log('[CreatorConsole] Fetching manuscripts...');
      const manuscriptsResponse = await axios.get(`${API}/creator/manuscripts`, config);
      const newManuscripts = manuscriptsResponse.data;
      console.log('=== [CreatorConsole] MANUSCRIPTS FETCHED ===');
      console.log('Count:', newManuscripts.length);
      console.log('Data:', JSON.stringify(newManuscripts.map(m => ({id: m.id, title: m.title}))));
      console.log('Setting state NOW...');
      setManuscripts(newManuscripts);
      console.log('State set complete');
      console.log('===========================================');

      // Load creator summary
      const summaryResponse = await axios.get(`${API}/creator/summary`, config);
      setMetrics({
        totalBooks: summaryResponse.data.total_books,
        totalActive: summaryResponse.data.total_active_books,
        totalEUs: Math.round(summaryResponse.data.total_engagement_units),
        totalReaderCredits: summaryResponse.data.total_reader_credits || 0
      });
      
      // Load author profile for upload modal
      try {
        const profileResponse = await axios.get(`${API}/author/author-profile`, config);
        if (profileResponse.data && profileResponse.data.exists !== false) {
          setAuthorProfile(profileResponse.data);
        }
      } catch (profileError) {
        console.log('[CreatorConsole] Author profile not found (may not exist yet)');
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch book history for a selected manuscript
  const fetchBookHistory = async (manuscriptId) => {
    if (!manuscriptId) return;
    
    setLoadingHistory(true);
    setBookHistory(null);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API}/manuscripts/${manuscriptId}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookHistory(response.data);
    } catch (error) {
      console.error('Error fetching book history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  // ============ TTS FUNCTIONS ============
  
  // NEW: Background async TTS generation
  const startTTSGenerationAsync = async (manuscriptId, devMode = true) => {
    console.log('[CreatorConsole] Starting ASYNC TTS generation for manuscript:', manuscriptId);
    
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(
        `${API}/v3/manuscripts/${manuscriptId}/generate-tts-async`,
        { mode: devMode ? 'dev' : 'full' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('[CreatorConsole] Background TTS job created:', response.data);
      
      // Show processing started modal
      setProcessingJobData(response.data);
      setShowProcessingStartedModal(true);
      
      // CRITICAL: Immediately update local state to 'processing' so polling starts right away
      // This prevents the race condition where the job completes before loadData() finishes
      console.log('[CreatorConsole] ⚡ Immediately setting manuscript to processing status');
      setManuscripts(prev => prev.map(m => {
        if (m.id === manuscriptId) {
          return {
            ...m,
            processing_status: 'processing',
            background_job_id: response.data.job_id,
            tts_generation: {
              ...m.tts_generation,
              status: 'in_progress',
              mode: devMode ? 'dev' : 'full',
              progress: {
                percent_complete: 0,
                segments_completed: 0,
                segments_total: response.data.segments
              }
            }
          };
        }
        return m;
      }));
      
      // Refresh manuscripts to get full updated data
      // (polling will handle live updates)
      await loadData();
      
      return response.data;
    } catch (error) {
      console.error('[CreatorConsole] Failed to start async TTS:', error);
      notifyError('Failed to start background audio generation');
      throw error;
    }
  };
  
  // OLD: Sync TTS generation (kept for backward compatibility/fallback)
  const startTTSGeneration = async (manuscriptId, devMode = false) => {
    console.log('[CreatorConsole] Starting TTS generation for manuscript:', manuscriptId);
    
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(
        `${API}/v3/manuscripts/${manuscriptId}/generate-tts`,
        { dev_mode: devMode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('[CreatorConsole] TTS generation started:', response.data);
      return response.data;
    } catch (error) {
      console.error('[CreatorConsole] Failed to start TTS generation:', error);
      notifyError('Failed to start audio generation');
      throw error;
    }
  };
  
  const startTTSRegeneration = async (manuscriptId, segmentIds, devMode = false) => {
    console.log('[CreatorConsole] Starting TTS regeneration for segments:', segmentIds);
    
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(
        `${API}/v3/manuscripts/${manuscriptId}/regenerate-tts`,
        { segment_ids: segmentIds, dev_mode: devMode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('[CreatorConsole] TTS regeneration started:', response.data);
      return response.data;
    } catch (error) {
      console.error('[CreatorConsole] Failed to start TTS regeneration:', error);
      notifyError('Failed to regenerate audio');
      throw error;
    }
  };
  
  const pollTTSProgress = async (manuscriptId) => {
    console.log('[CreatorConsole] Starting TTS progress polling for:', manuscriptId);
    
    let pollCount = 0;
    const maxPolls = 300; // 10 minutes max (300 * 2 seconds)
    
    const poll = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const response = await axios.get(
          `${API}/v3/manuscripts/${manuscriptId}/tts-status`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const { audio_status, segments_completed, segments_total } = response.data;
        
        console.log(`[CreatorConsole] TTS Progress: ${segments_completed}/${segments_total} (${audio_status})`);
        
        // Update progress state
        setTTSProgress({
          completed: segments_completed,
          total: segments_total,
          isComplete: audio_status === 'completed'
        });
        
        // Check if complete
        if (audio_status === 'completed') {
          console.log('[CreatorConsole] TTS generation complete!');
          setTimeout(() => {
            setShowTTSProgress(false);
            setShowCompletionModal(true);
            setTTSProgress({ completed: 0, total: 0, isComplete: false });
          }, 2000); // Show completion for 2 seconds
          return true; // Stop polling
        }
        
        if (audio_status === 'failed') {
          console.error('[CreatorConsole] TTS generation failed');
          notifyError('Audio generation failed');
          setShowTTSProgress(false);
          setTTSProgress({ completed: 0, total: 0, isComplete: false });
          return true; // Stop polling
        }
        
        // Continue polling
        pollCount++;
        if (pollCount < maxPolls) {
          setTimeout(() => poll(), 2000); // Poll every 2 seconds
        } else {
          console.warn('[CreatorConsole] TTS polling timeout');
          notifyError('Audio generation timed out');
          setShowTTSProgress(false);
        }
        
        return false; // Continue polling
        
      } catch (error) {
        console.error('[CreatorConsole] Error polling TTS status:', error);
        notifyError('Failed to check audio generation status');
        setShowTTSProgress(false);
        return true; // Stop polling on error
      }
    };
    
    // Start polling
    await poll();
  };
  
  // ============ END TTS FUNCTIONS ============

  const getStatusBadge = (manuscript) => {
    // NEW: Check processing_status first (for background TTS)
    if (manuscript.processing_status === 'processing' || manuscript.processing_status === 'queued') {
      // Progress comes from current_job, not tts_generation
      const progress = manuscript.current_job?.progress?.percent_complete || 0;
      return { 
        label: `Processing ${progress}%`, 
        color: 'bg-amber-900/40 text-amber-300 border border-amber-700/50',
        isProcessing: true
      };
    }
    
    if (manuscript.processing_status === 'failed' || manuscript.processing_status === 'partial') {
      return { 
        label: 'Failed - Retry Needed', 
        color: 'bg-red-900/40 text-red-300 border border-red-700/50',
        isFailed: true
      };
    }
    
    // Original status logic
    const status = manuscript.status || 'draft';
    
    switch(status) {
      case 'active':
        return { label: 'Active', color: 'bg-green-900/40 text-green-300 border border-green-700/50' };
      case 'ready':
        return { label: 'Ready', color: 'bg-blue-900/40 text-blue-300 border border-blue-700/50' };
      case 'audio_pending':
        return { label: 'Audio Pending', color: 'bg-yellow-900/40 text-yellow-300 border border-yellow-700/50' };
      case 'outline_pending':
        return { label: 'Outline Pending', color: 'bg-orange-900/40 text-orange-300 border border-orange-700/50' };
      case 'inactive':
        return { label: 'Inactive', color: 'bg-slate-800/40 text-slate-400 border border-slate-700/50' };
      case 'archived':
        return { label: 'Archived', color: 'bg-slate-800/40 text-slate-400 border border-slate-700/50' };
      default:
        return { label: 'Draft', color: 'bg-purple-900/40 text-purple-300 border border-purple-700/50' };
    }
  };

  const handleToggleActivation = (manuscript) => {
    // Determine action based on current state
    const action = manuscript.is_active ? 'deactivate' : 'activate';
    setActivationAction(action);
    setShowActivationModal(true);
  };

  const confirmToggleActivation = async () => {
    if (!selectedBook) return;

    const bookId = selectedBook.id;

    // Close the confirmation modal
    setShowActivationModal(false);

    // For activation, show progress modal
    if (activationAction === 'activate') {
      setActivationProgressSteps([
        { id: 'summary', label: 'Populating book summary...', status: 'pending' },
        { id: 'tts', label: 'Generating audio description...', status: 'pending' },
        { id: 'activate', label: 'Activating book...', status: 'pending' },
        { id: 'insights', label: 'Generating AI insights...', status: 'pending' }
      ]);
      setShowActivationProgress(true);
      
      try {
        const token = localStorage.getItem('accessToken');
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        };

        // Update step 1: Summary
        setActivationProgressSteps(prev => prev.map(s => 
          s.id === 'summary' ? { ...s, status: 'active' } : s
        ));
        setActivationProgressStep('Populating book summary from AI analysis...');
        
        // Small delay to show progress
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Update step 2: TTS (this happens during the API call)
        setActivationProgressSteps(prev => prev.map(s => 
          s.id === 'summary' ? { ...s, status: 'complete' } :
          s.id === 'tts' ? { ...s, status: 'active' } : s
        ));
        setActivationProgressStep('Generating audio description (this may take a moment)...');

        // Make the actual API call
        const activationResponse = await axios.patch(`${API}/creator/manuscripts/${bookId}/activate`, {}, config);
        const { ei_status, ei_message } = activationResponse.data;
        
        // Update step 3: Activate
        setActivationProgressSteps(prev => prev.map(s => 
          s.id === 'tts' ? { ...s, status: 'complete' } :
          s.id === 'activate' ? { ...s, status: 'active' } : s
        ));
        setActivationProgressStep('Finalizing activation...');
        
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Update step 3 complete, step 4: Insights
        setActivationProgressSteps(prev => prev.map(s => 
          s.id === 'activate' ? { ...s, status: 'complete' } :
          s.id === 'insights' ? { ...s, status: 'active' } : s
        ));
        
        // Handle EI status from backend response
        if (ei_status === 'queued') {
          setActivationProgressStep('Generating AI insights (runs in background)...');
          await new Promise(resolve => setTimeout(resolve, 500));
          // Mark as complete - EI runs async, no need to wait
          setActivationProgressSteps(prev => prev.map(s => 
            s.id === 'insights' ? { ...s, status: 'complete' } : s
          ));
        } else if (ei_status === 'skipped') {
          setActivationProgressStep(ei_message || 'AI insights skipped');
          await new Promise(resolve => setTimeout(resolve, 400));
          // Mark as complete (skipped is still a valid completion)
          setActivationProgressSteps(prev => prev.map(s => 
            s.id === 'insights' ? { ...s, status: 'complete', label: 'AI insights (skipped)' } : s
          ));
        } else {
          // Default: mark as complete
          setActivationProgressSteps(prev => prev.map(s => 
            s.id === 'insights' ? { ...s, status: 'complete' } : s
          ));
        }
        
        setActivationProgressStep('Book activated successfully!');
        
        // Wait a moment to show success, then close
        await new Promise(resolve => setTimeout(resolve, 800));
        setShowActivationProgress(false);
        
        // Success shown via progress modal - no toast needed
        
        // Refresh data
        await refreshDataAfterActivation(bookId, config);
        
      } catch (error) {
        console.error('Error activating book:', error);
        setShowActivationProgress(false);
        const detail = error.response?.data?.detail;
        let errorMsg = 'Failed to activate book';
        if (typeof detail === 'string') errorMsg = detail;
        else if (Array.isArray(detail) && detail.length > 0) errorMsg = detail[0]?.msg || 'Validation error';
        notifyError(errorMsg);
      }
    } else {
      // Deactivation is quick, no progress needed
      try {
        const token = localStorage.getItem('accessToken');
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        };
        
        await axios.patch(`${API}/creator/manuscripts/${bookId}/deactivate`, {}, config);
        // UI reflects change - no toast needed
        
        // Refresh data
        await refreshDataAfterActivation(bookId, config);
        
      } catch (error) {
        console.error('Error deactivating book:', error);
        const detail = error.response?.data?.detail;
        let errorMsg = 'Failed to deactivate book';
        if (typeof detail === 'string') errorMsg = detail;
        else if (Array.isArray(detail) && detail.length > 0) errorMsg = detail[0]?.msg || 'Validation error';
        notifyError(errorMsg);
      }
    }
  };

  const refreshDataAfterActivation = async (bookId, config) => {
    try {
      // Load fresh data
      const manuscriptsResponse = await axios.get(`${API}/creator/manuscripts`, config);
      const freshManuscripts = manuscriptsResponse.data;
      setManuscripts(freshManuscripts);

      // Load fresh summary
      const summaryResponse = await axios.get(`${API}/creator/summary`, config);
      setMetrics({
        totalBooks: summaryResponse.data.total_books,
        totalActive: summaryResponse.data.total_active_books,
        totalEUs: Math.round(summaryResponse.data.total_engagement_units),
        totalReaderCredits: summaryResponse.data.total_reader_credits || 0
      });

      // Update selected book with fresh data from the database
      const updatedBook = freshManuscripts.find(m => m.id === bookId);
      if (updatedBook) {
        setSelectedBook(updatedBook);
      }
    } catch (refreshError) {
      console.error('Error refreshing data:', refreshError);
    }
  };

  const formatNumber = (num) => {
    // Format whole numbers (for counts, words, etc.)
    return new Intl.NumberFormat('en-US').format(Math.round(num));
  };

  const formatEU = (num) => {
    // Format to 1 decimal place for EU stats only
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    }).format(num);
  };

  const handleOpenOutline = (manuscript) => {
    console.log('[CreatorConsole] Opening outline editor for manuscript:', manuscript.id);
    
    // For v3 manuscripts, use OutlineViewerModalV3
    if (manuscript.ingestion_version === 3) {
      setSelectedManuscriptIdV3(manuscript.id);
      // DO NOT set pendingCompletionData here - this is standalone mode
      // pendingCompletionData should only be set during upload flow
      setShowOutlineModalV3(true);
    } else {
      // Legacy manuscripts - would need old editor, but we can show a message
      notifyError('Please use the v3 upload flow for outline editing');
    }
  };

  const handlePreview = async (manuscript) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(
        `${API}/manuscripts/${manuscript.id}/open`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      const { session_id } = response.data;
      navigate(`/player/${session_id}`, { state: { returnTo: '/creator-console' } });
    } catch (error) {
      console.error('Error opening manuscript:', error);
      notifyError('Failed to open preview. Please try again.');
    }
  };

  const handleEditMetadata = (manuscript) => {
    setSelectedBook(manuscript);
    setShowEditModal(true);
  };

  const handleArchive = async () => {
    if (!selectedBook) return;
    
    try {
      const token = localStorage.getItem('accessToken');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };
      
      const isArchived = selectedBook.status === 'archived';
      
      // Cancel any active background processing before archiving
      if (!isArchived && selectedBook.processing_status === 'processing' && selectedBook.background_job_id) {
        console.log('[CreatorConsole] Cancelling active background job before archive');
        try {
          await axios.post(
            `${API}/v3/tts-jobs/${selectedBook.background_job_id}/cancel`,
            {},
            config
          );
        } catch (cancelError) {
          console.error('[CreatorConsole] Failed to cancel job:', cancelError);
        }
      }
      
      await axios.patch(`${API}/manuscripts/${selectedBook.id}/${isArchived ? 'unarchive' : 'archive'}`, {}, config);
      // UI updates via loadData - no toast needed
      setShowArchiveModal(false);
      
      // Refresh data
      await loadData();
    } catch (error) {
      console.error('Error archiving/unarchiving manuscript:', error);
      notifyError('Failed to update book status');
    }
  };

  const handleDelete = async () => {
    if (!selectedBook) return;
    
    try {
      const token = localStorage.getItem('accessToken');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };
      
      // NEW: Check if there's an active background job and cancel it first
      if (selectedBook.processing_status === 'processing' && selectedBook.background_job_id) {
        console.log('[CreatorConsole] Cancelling active background job:', selectedBook.background_job_id);
        
        try {
          await axios.post(
            `${API}/v3/tts-jobs/${selectedBook.background_job_id}/cancel`,
            {},
            config
          );
          console.log('[CreatorConsole] Cancelled background processing');
        } catch (cancelError) {
          console.error('[CreatorConsole] Failed to cancel job:', cancelError);
          // Continue with deletion even if cancel fails
        }
      }
      
      // SOFT DELETE - archives the manuscript (works in production)
      // Use standard delete endpoint without hard_delete flag
      await axios.delete(`${API}/manuscripts/${selectedBook.id}`, config);
      
      // Deletion confirmed via modal close - no toast needed
      
      setShowDeleteModal(false);
      setSelectedBook(null);
      
      // Refresh data
      await loadData();
    } catch (error) {
      console.error('Error deleting manuscript:', error);
      notifyError('Failed to delete book');
    }
  };

  const handleTestEmail = async () => {
    if (!selectedBook) return;
    
    try {
      const token = localStorage.getItem('accessToken');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };
      
      console.log('[CreatorConsole] Sending test emails...');
      
      // Use the test-email endpoint for all manuscript versions
      const endpoint = `${API}/manuscripts/${selectedBook.id}/test-email`;
      
      await axios.post(endpoint, {}, config);
      // Email sent - could show a subtle confirmation but not blocking
      console.log('[CreatorConsole] Test emails sent successfully');
    } catch (error) {
      console.error('Error sending test email:', error);
      notifyError('Failed to send test emails');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white">
      {/* Creator Navigation */}
      <CreatorNav />
      <CreatorTopMenuBar />

      {/* Main Content with mobile bottom padding */}
      <div className="pb-20 md:pb-0">
        <div className="container mx-auto px-4 py-6 max-w-6xl pt-6">
        
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-2">Author Console</h1>
          <p className="text-slate-400">Manage your NFD-SmartBooks<sup className="text-[0.6em]">™</sup> and track engagement</p>
        </div>

        {/* At a Glance Metrics */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-white mb-4">At a Glance</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-xl p-6 text-white shadow-lg border border-purple-500/20 backdrop-blur-sm">
              <div className="flex items-center justify-between mb-2">
                <BarChart3 className="w-8 h-8 opacity-80" />
                <span className="text-3xl font-bold">{formatNumber(metrics.totalBooks)}</span>
              </div>
              <p className="text-purple-100 font-medium">Total Books</p>
            </div>
            
            <div className="bg-gradient-to-br from-blue-600 to-cyan-600 rounded-xl p-6 text-white shadow-lg border border-blue-500/20 backdrop-blur-sm">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="w-8 h-8 opacity-80" />
                <span className="text-3xl font-bold">{formatNumber(metrics.totalActive)}</span>
              </div>
              <p className="text-blue-100 font-medium">Active Books</p>
            </div>
            
            <div className="bg-gradient-to-br from-emerald-600 to-teal-600 rounded-xl p-6 text-white shadow-lg border border-emerald-500/20 backdrop-blur-sm">
              <div className="flex items-center justify-between mb-2">
                <Award className="w-8 h-8 opacity-80" />
                <span className="text-3xl font-bold">{(metrics.totalReaderCredits || 0).toFixed(2)}</span>
              </div>
              <p className="text-emerald-100 font-medium">Total Reader Credits</p>
            </div>
          </div>
        </section>

        {/* Upload New Manuscript CTA */}
        <section className="mb-8">
          <div className="bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-600 rounded-xl p-6 text-white hover:shadow-xl transition-shadow border border-purple-500/20">
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
              <div className="flex items-center gap-4 flex-1">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Upload className="w-8 h-8" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold mb-1">Upload New EPUB</h3>
                  <p className="text-purple-100">Turn your manuscript into an NFD-SmartBook<sup className="text-[0.6em]">™</sup> in minutes.</p>
                </div>
              </div>
              <Button
                onClick={() => setShowStreamlinedUpload(true)}
                className="bg-white text-purple-600 hover:bg-gray-100 font-semibold px-6 w-full lg:w-auto"
                size="lg"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add A Book to Your Library
              </Button>
            </div>
          </div>
        </section>

        {/* Your Books Management */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-white mb-4">Your Books</h2>
          
          {loading ? (
            <div className="text-center py-8 text-slate-400">Loading...</div>
          ) : manuscripts.length === 0 ? (
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl shadow-md p-8 text-center backdrop-blur-sm">
              <p className="text-slate-300 mb-4">You haven&apos;t uploaded any manuscripts yet</p>
              <Button 
                onClick={() => setShowStreamlinedUpload(true)}
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
              >
                Upload Your First Book
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {manuscripts.map(manuscript => {
                const status = getStatusBadge(manuscript);
                return (
                  <Link 
                    key={manuscript.id}
                    to={`/author/titles/${manuscript.id}`}
                    className="block bg-slate-800/50 border border-slate-700 rounded-xl shadow-md p-5 hover:shadow-lg hover:border-indigo-500/50 transition-all cursor-pointer backdrop-blur-sm"
                  >
                    <div className="flex items-start gap-4">
                      {/* Cover Thumbnail - using shared cover utility */}
                      <div className="w-16 h-20 rounded-lg bg-slate-700 flex-shrink-0 overflow-hidden">
                        {hasCoverImage(manuscript) ? (
                          <img 
                            src={getBookCoverUrl(manuscript, BACKEND_URL)} 
                            alt={manuscript.title} 
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div className={`w-full h-full items-center justify-center ${hasCoverImage(manuscript) ? 'hidden' : 'flex'}`}>
                          <BookOpen className="w-6 h-6 text-slate-500" />
                        </div>
                      </div>
                      
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-white truncate">{manuscript.title}</h3>
                        <p className="text-sm text-slate-400 truncate">{manuscript.author}</p>
                        {manuscript.isbn && (
                          <p className="text-xs text-slate-500 mt-1">ISBN: {manuscript.isbn}</p>
                        )}
                        <div className="mt-2 flex items-center gap-3 flex-wrap">
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${status.color}`}>
                            {status.isProcessing && <Loader2 className="w-3 h-3 animate-spin" />}
                            {status.label}
                          </span>
                          {/* Audio Status Badge */}
                          {manuscript.audio_status === 'processing' && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-purple-900/40 text-purple-300 border border-purple-700/50">
                              <Volume2 className="w-3 h-3 animate-pulse" />
                              Audio Processing
                            </span>
                          )}
                          {(manuscript.audio_status === 'completed' || manuscript.audio_status === 'partial') && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-green-900/40 text-green-300 border border-green-700/50">
                              <Volume2 className="w-3 h-3" />
                              Audio Ready
                            </span>
                          )}
                          {/* {manuscript.api_cost_total > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-900/40 text-amber-300 border border-amber-700/50">
                              <DollarSign className="w-3 h-3" />
                              ${manuscript.api_cost_total.toFixed(4)}
                            </span>
                          )} */}
                          {/* Reader Credits Badge */}
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-cyan-900/40 text-cyan-300 border border-cyan-700/50">
                            <Award className="w-3 h-3" />
                            {(manuscript.total_reader_credits || 0).toFixed(2)} Credits
                          </span>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <span className="px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium transition-colors flex items-center gap-2">
                          <BookOpen className="w-4 h-4" />
                          Book Details
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setSelectedBook(manuscript);
                            setShowArchiveModal(true);
                          }}
                          className="p-2 rounded-lg bg-slate-700 hover:bg-amber-900/50 hover:text-amber-400 text-slate-400 transition-colors"
                          title="Archive"
                        >
                          <Archive className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setSelectedBook(manuscript);
                            setShowDeleteModal(true);
                          }}
                          className="p-2 rounded-lg bg-slate-700 hover:bg-red-900/50 hover:text-red-400 text-slate-400 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </section>

        {/* Anchor Point for Book Details */}
        <div ref={bookDetailsAnchorRef} className="scroll-mt-20" />

        {/* Selected Book Detail Card */}
        {selectedBook && (
          <section className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-white">Book Details</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedBook(null)}
                className="text-slate-400 hover:text-white hover:bg-slate-800"
              >
                ✕ Close
              </Button>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl shadow-xl overflow-hidden backdrop-blur-sm">
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 p-3">
                {/* Cover Image - Reduced by 37.5% from original */}
                <div className="lg:col-span-1 space-y-2 max-w-[250px] mx-auto lg:mx-0">
                  <div 
                    className="aspect-[2/3] bg-gradient-to-br from-purple-200 to-cyan-200 rounded-lg overflow-hidden shadow-lg cursor-pointer hover:shadow-2xl transition-shadow"
                    onClick={handleOpenPreview}
                  >
                    <img
                      src={selectedBook.cover_image_path ? `${BACKEND_URL}/api/covers/${selectedBook.id}?v=${selectedBook.cover_updated_at || selectedBook.updated_at || Date.now()}` : '/newaudia-logo.jpg'}
                      alt={selectedBook.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  {/* Edit, Archive, Delete, Test Email Buttons */}
                  <div className="grid grid-cols-2 gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs h-8"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/author-analytics/${selectedBook.id}`);
                      }}
                    >
                      <BarChart3 className="w-3 h-3 mr-1" />
                      Analytics
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs h-8"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditMetadata(selectedBook);
                      }}
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs h-8"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowArchiveModal(true);
                      }}
                    >
                      <Archive className="w-3 h-3 mr-1" />
                      Archive
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs h-8 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTestEmail();
                      }}
                    >
                      <Mail className="w-3 h-3 mr-1" />
                      Test Email
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs h-8 text-red-600 hover:text-red-700 hover:bg-red-50 col-span-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowDeleteModal(true);
                      }}
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Delete
                    </Button>
                  </div>

                  {/* Activation Toggle - Moved below Delete button */}
                  <div className="mt-3">
                    {(selectedBook.processing_status === 'processing' || selectedBook.processing_status === 'queued') ? (
                      <div className="text-center bg-amber-900/30 border border-amber-700/30 rounded-lg py-3">
                        <Loader2 className="h-4 w-4 animate-spin mx-auto mb-1 text-amber-400" />
                        <p className="text-amber-400 text-xs font-semibold">Audio Processing...</p>
                        <p className="text-slate-400 text-xs mt-1">
                          {selectedBook.current_job?.progress?.percent_complete || 0}% complete
                        </p>
                      </div>
                    ) : (selectedBook.processing_status === 'failed' || selectedBook.processing_status === 'partial') ? (
                      <div className="text-center bg-red-900/30 border border-red-700/30 rounded-lg py-3">
                        <p className="text-red-400 text-xs font-semibold">⚠️ Audio Generation Failed</p>
                        <p className="text-slate-400 text-xs mt-1">Contact support for assistance</p>
                      </div>
                    ) : (selectedBook.audio_status === 'completed' || selectedBook.tts_generation_status === 'complete' || selectedBook.tts_generation_status === 'completed' || selectedBook.processing_status === 'completed') ? (
                      <Button
                        className={`w-full h-12 text-white font-bold text-base shadow-lg ${
                          selectedBook.is_active 
                            ? 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 border border-red-500' 
                            : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 border border-green-500'
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleActivation(selectedBook);
                        }}
                      >
                        {selectedBook.is_active ? (
                          <>🔴 DEACTIVATE MANUSCRIPT</>
                        ) : (
                          <>🟢 ACTIVATE MANUSCRIPT</>
                        )}
                      </Button>
                    ) : (
                      <div className="text-center bg-slate-900/50 border border-slate-700/30 rounded-lg py-2">
                        <p className="text-slate-400 text-xs">Complete audio generation to activate this book</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Book Info */}
                <div className="lg:col-span-3 space-y-2">
                  <div>
                    <h3 className="text-xl font-bold text-white mb-1">{selectedBook.title}</h3>
                    <p className="text-base text-slate-400 mb-2">{selectedBook.author}</p>
                    
                    {/* Status Badge */}
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadge(selectedBook).color}`}>
                        {getStatusBadge(selectedBook).label}
                      </span>
                      {selectedBook.is_active && (
                        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-900/40 text-green-300 border border-green-700/50">
                          📡 Live in Library
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Metadata Grid */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-slate-900/50 border border-slate-700/30 rounded-lg p-2">
                      <div className="text-xs text-slate-500 mb-0.5">Publisher</div>
                      <div className="font-semibold text-slate-200 text-sm">{selectedBook.publisher || 'Independent'}</div>
                    </div>
                    <div className="bg-slate-900/50 border border-slate-700/30 rounded-lg p-2">
                      <div className="text-xs text-slate-500 mb-0.5">ISBN</div>
                      <div className="font-semibold text-slate-200 text-sm">{selectedBook.isbn || 'Not provided'}</div>
                    </div>
                    <div className="bg-slate-900/50 border border-slate-700/30 rounded-lg p-2">
                      <div className="text-xs text-slate-500 mb-0.5">Chapters</div>
                      <div className="font-semibold text-slate-200 text-sm">{selectedBook.chapter_count || 'N/A'}</div>
                    </div>
                    <div className="bg-slate-900/50 border border-slate-700/30 rounded-lg p-2">
                      <div className="text-xs text-slate-500 mb-0.5">Total Words</div>
                      <div className="font-semibold text-slate-200 text-sm">{formatNumber(selectedBook.canonical_word_count || 0)}</div>
                    </div>
                    <div className="bg-slate-900/50 border border-slate-700/30 rounded-lg p-2">
                      <div className="text-xs text-slate-500 mb-0.5">Genre</div>
                      <div className="font-semibold text-slate-200 text-sm capitalize">{selectedBook.genre || 'Not specified'}</div>
                    </div>
                    <div className="bg-slate-900/50 border border-slate-700/30 rounded-lg p-2">
                      <div className="text-xs text-slate-500 mb-0.5">Theme</div>
                      <div className="font-semibold text-slate-200 text-sm capitalize">{selectedBook.theme || 'Not specified'}</div>
                    </div>
                    <div className="bg-slate-900/50 border border-slate-700/30 rounded-lg p-2">
                      <div className="text-xs text-slate-500 mb-0.5">Created</div>
                      <div className="font-semibold text-slate-200 text-sm">
                        {selectedBook.created_at ? new Date(selectedBook.created_at).toLocaleDateString() : 'N/A'}
                      </div>
                    </div>
                    <div className="bg-slate-900/50 border border-slate-700/30 rounded-lg p-2">
                      <div className="text-xs text-slate-500 mb-0.5">Purchase Book</div>
                      {selectedBook.purchase_url ? (
                        <a 
                          href={selectedBook.purchase_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="font-semibold text-purple-400 hover:text-purple-300 flex items-center gap-1 text-sm"
                        >
                          Order Paperback
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <div className="font-semibold text-slate-500 text-sm">Not available</div>
                      )}
                    </div>
                  </div>

                  {/* Engagement Stats */}
                  <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-700/50 rounded-lg p-3 shadow-lg">
                    <h4 className="font-bold text-purple-300 mb-3 flex items-center gap-1 text-sm">
                      <Award className="w-4 h-4" />
                      Engagement Performance
                    </h4>
                    
                    {/* Total and Breakdown */}
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div className="bg-purple-950/40 rounded-lg p-2 text-center border border-purple-700/30">
                        <div className="text-xl font-bold text-purple-400">
                          {formatEU(selectedBook.total_engagement_units || 0)}
                        </div>
                        <div className="text-xs text-slate-400">Total EUs</div>
                      </div>
                      <div className="bg-slate-900/60 rounded-lg p-2 border border-slate-700/30">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-slate-400">Passive:</span>
                            <span className="text-sm font-bold text-blue-400">{formatEU(selectedBook.passive_engagement_units || 0)}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-slate-400">Active:</span>
                            <span className="text-sm font-bold text-cyan-400">{formatEU(selectedBook.active_engagement_units || 0)}</span>
          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Other Stats */}
                    <div className="grid grid-cols-2 gap-2">
                      <div className="text-center bg-slate-900/40 rounded-lg p-2 border border-slate-700/20">
                        <div className="text-lg font-bold text-slate-300">
                          {formatNumber((selectedBook.total_listen_seconds || 0) / 60)}
                        </div>
                        <div className="text-xs text-slate-400">Minutes Played</div>
                      </div>
                      <div className="text-center bg-slate-900/40 rounded-lg p-2 border border-slate-700/20">
                        <div className="text-lg font-bold text-slate-300">
                          {formatNumber(selectedBook.total_words_consumed || 0)}
                        </div>
                        <div className="text-xs text-slate-400">Words Consumed</div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="outline"
                      className="h-9"
                      disabled={selectedBook.processing_status === 'processing' || selectedBook.processing_status === 'queued'}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenOutline(selectedBook);
                      }}
                    >
                      {(selectedBook.processing_status === 'processing' || selectedBook.processing_status === 'queued') ? (
                        <>
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <Edit className="w-3 h-3 mr-1" />
                          Edit Outline
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      className="h-9"
                      disabled={selectedBook.processing_status === 'processing' || selectedBook.processing_status === 'queued'}
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePreview(selectedBook);
                      }}
                    >
                      {(selectedBook.processing_status === 'processing' || selectedBook.processing_status === 'queued') ? (
                        <>
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <Eye className="w-3 h-3 mr-1" />
                          Preview Book
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Book History Card - Full width below Book Details */}
            <div className="mt-4 bg-slate-800/50 border border-slate-700 rounded-xl shadow-xl overflow-hidden backdrop-blur-sm p-4">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-indigo-400" />
                Book History
              </h3>
              {loadingHistory ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                </div>
              ) : bookHistory ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-slate-900/50 rounded-xl border border-slate-700/30">
                    <p className="text-xs text-slate-400 mb-1">Upload Date</p>
                    <p className="text-lg font-semibold text-slate-200">
                      {bookHistory.upload_date 
                        ? new Date(bookHistory.upload_date).toLocaleDateString()
                        : 'N/A'}
                    </p>
                  </div>
                  <div className="text-center p-3 bg-slate-900/50 rounded-xl border border-slate-700/30">
                    <p className="text-xs text-slate-400 mb-1">Date Last Accessed</p>
                    <p className="text-lg font-semibold text-slate-200">
                      {bookHistory.date_last_accessed 
                        ? new Date(bookHistory.date_last_accessed).toLocaleDateString()
                        : 'Never'}
                    </p>
                  </div>
                  <div className="text-center p-3 bg-slate-900/50 rounded-xl border border-slate-700/30">
                    <p className="text-xs text-slate-400 mb-1">Days Available</p>
                    <p className="text-lg font-semibold text-slate-200">{bookHistory.days_available || 0}</p>
                  </div>
                  <div className="text-center p-3 bg-slate-900/50 rounded-xl border border-slate-700/30">
                    <p className="text-xs text-slate-400 mb-1 flex items-center justify-center gap-1">
                      <Users className="w-3 h-3" /> Unique Readers
                    </p>
                    <p className="text-lg font-semibold text-indigo-400">{bookHistory.unique_readers || 0}</p>
                  </div>
                  <div className="text-center p-3 bg-slate-900/50 rounded-xl border border-slate-700/30">
                    <p className="text-xs text-slate-400 mb-1">Guest Readers</p>
                    <p className="text-lg font-semibold text-purple-400">{bookHistory.guest_readers || 0}</p>
                  </div>
                  <div className="text-center p-3 bg-slate-900/50 rounded-xl border border-slate-700/30">
                    <p className="text-xs text-slate-400 mb-1">Avg. Readers/Day</p>
                    <p className="text-lg font-semibold text-slate-200">{bookHistory.avg_readers_per_day || 0}</p>
                  </div>
                  <div className="text-center p-3 bg-slate-900/50 rounded-xl border border-slate-700/30">
                    <p className="text-xs text-slate-400 mb-1">Total Words Read</p>
                    <p className="text-lg font-semibold text-emerald-400">{(bookHistory.total_words_read || 0).toLocaleString()}</p>
                  </div>
                  <div className="text-center p-3 bg-slate-900/50 rounded-xl border border-slate-700/30">
                    <p className="text-xs text-slate-400 mb-1">Engagement (mins)</p>
                    <p className="text-lg font-semibold text-cyan-400">{bookHistory.total_engagement_minutes || 0}</p>
                  </div>
                  <div className="text-center p-3 bg-slate-900/50 rounded-xl border border-slate-700/30">
                    <p className="text-xs text-slate-400 mb-1 flex items-center justify-center gap-1">
                      <MessageSquare className="w-3 h-3" /> Audia Questions
                    </p>
                    <p className="text-lg font-semibold text-amber-400">{bookHistory.total_audia_questions || 0}</p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-2">No history data available</p>
              )}
            </div>
          </section>
        )}
        </div>
        {/* End of container */}
      </div>
      {/* End of pb-20 wrapper */}

      {/* Mobile Bottom Navigation */}
      <CreatorMobileBottomNav />

      {/* Delete Confirmation Modal */}
      <AlertDialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-bold text-red-600">
              {selectedBook?.processing_status === 'processing' ? 'Cancel Processing & Delete Book' : 'Delete Book'}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-base pt-2">
                <p className="font-semibold text-gray-900 mb-2">
                  Are you sure you want to delete &quot;{selectedBook?.title}&quot;?
                </p>
                
                {/* NEW: Active processing warning */}
                {selectedBook?.processing_status === 'processing' && (
                  <div className="bg-amber-50 border-l-4 border-amber-500 p-3 rounded mb-3">
                    <p className="text-amber-900 font-medium text-sm flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Active Background Processing
                    </p>
                    <p className="text-amber-800 text-sm mt-1">
                      This book is currently being processed ({selectedBook?.tts_generation?.progress?.percent_complete || 0}% complete). 
                      Deleting will cancel the job and stop any further API charges.
                    </p>
                  </div>
                )}
                
                <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded">
                  <p className="text-red-900 font-medium text-sm">⚠️ HARD DELETE (Development Mode):</p>
                  <ul className="text-red-800 text-sm mt-1 space-y-1">
                    {selectedBook?.processing_status === 'processing' && (
                      <li>• <strong>Background processing will be cancelled</strong></li>
                    )}
                    <li>• This action cannot be undone</li>
                    <li>• All book data and audio will be permanently deleted</li>
                    <li>• All reader library entries and EU records will be removed</li>
                    <li>• All sessions will be deleted</li>
                    <li>• Physical files (EPUB, audio, cover) will be deleted</li>
                  </ul>
                  <p className="text-xs text-red-700 mt-2 italic">
                    This is enabled for development/testing. Will be disabled in production.
                  </p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-3">
            <AlertDialogCancel className="flex-1">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
              className="flex-1 bg-red-600 hover:bg-red-700"
            >
              {selectedBook?.processing_status === 'processing' 
                ? 'Cancel Processing & Delete' 
                : 'Delete Permanently'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Archive Confirmation Modal */}
      <AlertDialog open={showArchiveModal} onOpenChange={setShowArchiveModal}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-bold">
              {selectedBook?.status === 'archived' ? 'Unarchive Book' : 'Archive Book'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base pt-2">
              <p className="font-semibold text-gray-900 mb-2">
                {selectedBook?.status === 'archived' 
                  ? `Restore "${selectedBook?.title}" to your active library?`
                  : `Move "${selectedBook?.title}" to archive?`
                }
              </p>
              <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded">
                <p className="text-blue-900 font-medium text-sm">
                  {selectedBook?.status === 'archived' ? 'ℹ️ Unarchiving will:' : 'ℹ️ Archiving will:'}
                </p>
                <ul className="text-blue-800 text-sm mt-1 space-y-1">
                  {selectedBook?.status === 'archived' ? (
                    <>
                      <li>• Make the book visible in your active library</li>
                      <li>• Allow you to edit and activate it</li>
                    </>
                  ) : (
                    <>
                      <li>• Hide the book from your main library</li>
                      <li>• Automatically deactivate it from the public catalog</li>
                      <li>• You can restore it anytime</li>
                    </>
                  )}
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-3">
            <AlertDialogCancel className="flex-1">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleArchive}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              {selectedBook?.status === 'archived' ? 'Unarchive' : 'Archive'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Streamlined Upload Flow - Production upload for authors */}
      <StreamlinedUploadFlow
        isOpen={showStreamlinedUpload}
        onClose={() => setShowStreamlinedUpload(false)}
        onComplete={async () => {
          console.log('[CreatorConsole] Streamlined upload complete');
          setShowStreamlinedUpload(false);
          // Refresh manuscript list
          await loadData();
        }}
      />

      {/* Edit Metadata Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <EditMetadataForm
            manuscript={selectedBook}
            onClose={() => setShowEditModal(false)}
            onSuccess={async () => {
              await loadData(); // Refresh the manuscript list
              // Also refresh the selected book with updated data
              if (selectedBook?.id) {
                try {
                  const response = await axios.get(`${API}/manuscripts/${selectedBook.id}`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
                  });
                  setSelectedBook(response.data);
                } catch (err) {
                  console.error('Error refreshing book data:', err);
                }
              }
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Activation Confirmation Modal */}
      <AlertDialog open={showActivationModal} onOpenChange={setShowActivationModal}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-2xl font-bold">
              {activationAction === 'activate' ? '🟢 Activate Book' : '🔴 Deactivate Book'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base space-y-3 pt-2">
              {activationAction === 'activate' ? (
                <>
                  <p className="font-semibold text-gray-900">
                    This will make &quot;{selectedBook?.title}&quot; available to all readers in the NewAudia Library.
                  </p>
                  <div className="bg-green-50 border-l-4 border-green-500 p-3 rounded">
                    <p className="text-green-900 font-medium">✅ Benefits:</p>
                    <ul className="text-green-800 text-sm mt-2 space-y-1">
                      <li>• Visible to all readers</li>
                      <li>• Start earning Engagement Units (EUs)</li>
                      <li>• Track reader engagement in real-time</li>
                      <li>• Monetization enabled</li>
                    </ul>
                  </div>
                  <p className="text-sm text-gray-600">
                    You can deactivate this book at any time.
                  </p>
                </>
              ) : (
                <>
                  <p className="font-semibold text-gray-900">
                    This will remove &quot;{selectedBook?.title}&quot; from the NewAudia Library.
                  </p>
                  <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded">
                    <p className="text-red-900 font-medium">⚠️ Effects:</p>
                    <ul className="text-red-800 text-sm mt-2 space-y-1">
                      <li>• Hidden from all readers</li>
                      <li>• No new engagement or EUs</li>
                      <li>• Existing readers can still finish their sessions</li>
                      <li>• Monetization paused</li>
                    </ul>
                  </div>
                  <p className="text-sm text-gray-600">
                    You can reactivate this book at any time.
                  </p>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-3">
            <AlertDialogCancel className="flex-1">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmToggleActivation}
              className={`flex-1 ${
                activationAction === 'activate'
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-red-600 hover:bg-red-700'
              }`}
            >
              {activationAction === 'activate' ? 'Activate' : 'Deactivate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Activation Progress Modal */}
      <Dialog open={showActivationProgress} onOpenChange={() => {}}>
        <DialogContent className="max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin text-green-600" />
              Activating Book
            </DialogTitle>
            <DialogDescription asChild>
              <div className="pt-4 space-y-4">
                <p className="text-gray-600">
                  Preparing &quot;{selectedBook?.title}&quot; for the public library...
                </p>
                
                {/* Progress Steps */}
                <div className="space-y-3">
                  {activationProgressSteps.map((step) => (
                    <div key={step.id} className="flex items-center gap-3">
                      {step.status === 'pending' && (
                        <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
                      )}
                      {step.status === 'active' && (
                        <Loader2 className="w-5 h-5 animate-spin text-green-600" />
                      )}
                      {step.status === 'complete' && (
                        <div className="w-5 h-5 rounded-full bg-green-600 flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                      <span className={`text-sm ${
                        step.status === 'complete' ? 'text-green-700 font-medium' :
                        step.status === 'active' ? 'text-gray-900 font-medium' :
                        'text-gray-500'
                      }`}>
                        {step.label}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Current Step Detail */}
                {activationProgressStep && (
                  <div className="bg-gray-50 rounded-lg p-3 mt-4">
                    <p className="text-sm text-gray-600">{activationProgressStep}</p>
                  </div>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>

      {/* V3 Outline Viewer Modal */}
      <OutlineViewerModalV3
        isOpen={showOutlineModalV3}
        onClose={() => {
          console.log('[CreatorConsole] V3 Outline editor closed unexpectedly');
          setShowOutlineModalV3(false);
          setSelectedManuscriptIdV3(null);
          setPendingCompletionData(null);
          loadData();
        }}
        onSaveAndContinue={async (data) => {
          console.log('[CreatorConsole] Outline save complete:', data);
          setShowOutlineModalV3(false);
          
          // Check if this is from upload flow (has pendingCompletionData) or standalone
          const isFromUpload = !!pendingCompletionData && !data?.needsTTS;
          
          if (isFromUpload) {
            // Upload flow - show TTS decision modal
            console.log('[CreatorConsole] Upload flow - showing TTS decision modal');
            
            // CRITICAL: Refresh manuscript data to get updated segment counts after edits
            try {
              const token = localStorage.getItem('accessToken');
              const response = await axios.get(
                `${API}/v3/manuscripts/${selectedManuscriptIdV3}/outline`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              
              const outlineData = response.data;
              console.log('[CreatorConsole] Refreshed outline data:', outlineData);
              
              // Update pendingCompletionData with fresh segment/word counts
              setPendingCompletionData(prev => ({
                ...prev,
                segment_count: outlineData.segment_count,
                word_count: outlineData.total_word_count,
                character_count: outlineData.total_character_count,
                chapter_count: outlineData.segment_count
              }));
            } catch (error) {
              console.error('[CreatorConsole] Failed to refresh outline data:', error);
            }
            
            setShowTTSModal(true);
          } else if (data?.needsTTS) {
            // Standalone with changes - start TTS regeneration
            console.log('[CreatorConsole] Standalone mode - starting TTS regeneration');
            console.log('[CreatorConsole] Modified segments:', data.modifiedSegments);
            // Progress modal shows status - no toast needed
            
            setShowTTSProgress(true);
            setIsTTSRegeneration(true); // This is regeneration
            setTTSProgress({ 
              completed: 0, 
              total: data.modifiedSegments?.length || 1, 
              isComplete: false 
            });
            
            try {
              // Start TTS regeneration for only the modified segments
              // Use FULL mode (dev_mode=false) for manual edits - creators are making final touches
              await startTTSRegeneration(
                selectedManuscriptIdV3, 
                data.modifiedSegments || [],
                false  // FULL TTS generation, not dev mode
              );
              
              // Poll for progress
              await pollTTSProgress(selectedManuscriptIdV3);
              
              // After completion, refresh data
              setSelectedManuscriptIdV3(null);
              setPendingCompletionData(null);
              loadData();
              
            } catch (error) {
              console.error('[CreatorConsole] TTS regeneration failed:', error);
              setShowTTSProgress(false);
              setSelectedManuscriptIdV3(null);
              setPendingCompletionData(null);
              loadData();
            }
          } else {
            // No TTS needed - just close and refresh
            console.log('[CreatorConsole] No TTS needed - closing');
            setSelectedManuscriptIdV3(null);
            setPendingCompletionData(null);
            loadData();
          }
        }}
        manuscriptId={selectedManuscriptIdV3}
        isStandalone={!pendingCompletionData}
      />

      {/* TTS Decision Modal */}
      <TTSDecisionModal
        isOpen={showTTSModal}
        manuscriptData={pendingCompletionData}
        onYes={async (ttsMode) => {
          console.log('[CreatorConsole] User selected: Generate TTS in', ttsMode, 'mode');
          setShowTTSModal(false);
          
          try {
            // NEW: Start ASYNC TTS generation (background processing)
            const devMode = ttsMode === 'dev';
            await startTTSGenerationAsync(selectedManuscriptIdV3, devMode);
            
            // Background processing started - completion modal shows status
            console.log(`[CreatorConsole] Background audio processing started (${ttsMode === 'dev' ? 'Dev Mode' : 'Full Production'})`);
            
            // Show completion modal (user can continue working)
            setShowCompletionModal(true);
            
          } catch (error) {
            console.error('[CreatorConsole] Failed to start background TTS:', error);
            setShowCompletionModal(true); // Show completion anyway
          }
        }}
        onNo={() => {
          console.log('[CreatorConsole] User selected: Skip TTS');
          setShowTTSModal(false);
          setShowCompletionModal(true);
        }}
      />

      {/* Upload Completion Modal - shown after TTS decision */}
      {showCompletionModal && pendingCompletionData && (
        <UploadCompletionModal
          isOpen={showCompletionModal}
          manuscriptData={pendingCompletionData}
          enrichmentStatus="none"
          onClose={() => {
            console.log('[CreatorConsole] Completion modal closed');
            setShowCompletionModal(false);
            setPendingCompletionData(null);
            setSelectedManuscriptIdV3(null);
            loadData();
          }}
        />
      )}

      {/* TTS Progress Modal - shown during TTS generation or regeneration */}
      <TTSProgressModal
        isOpen={showTTSProgress}
        segmentsTotal={ttsProgress.total}
        segmentsCompleted={ttsProgress.completed}
        isComplete={ttsProgress.isComplete}
        isRegeneration={isTTSRegeneration}
      />

      {/* Book Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedBook && (
            <div className="space-y-6">
              {/* Header */}
              <div className="text-center space-y-4">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                  {selectedBook.title}
                </h2>
                {selectedBook.subtitle && (
                  <p className="text-lg text-gray-600 dark:text-gray-400">
                    {selectedBook.subtitle}
                  </p>
                )}
                <p className="text-base text-gray-700 dark:text-gray-300 font-medium">
                  by {selectedBook.author}
                </p>
              </div>

              {/* Cover Image - Reduced by 37.5% from original */}
              <div className="flex justify-center">
                <div className="w-30 aspect-[2/3] rounded-lg overflow-hidden shadow-2xl">
                  <img
                    src={selectedBook.cover_image_path ? `${BACKEND_URL}/api/covers/${selectedBook.id}?v=${selectedBook.cover_updated_at || selectedBook.updated_at || Date.now()}` : '/newaudia-logo.jpg'}
                    alt={selectedBook.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Genre/Theme Badges */}
              <div className="flex flex-wrap justify-center gap-3">
                {selectedBook.genre && (
                  <span className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-full text-sm font-semibold shadow-lg">
                    📚 {selectedBook.genre}
                  </span>
                )}
                {selectedBook.theme && (
                  <span className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-cyan-500 to-cyan-600 text-white rounded-full text-sm font-semibold shadow-lg">
                    🎯 {selectedBook.theme}
                  </span>
                )}
              </div>

              {/* Close Button */}
              <div className="flex justify-center pt-4">
                <Button
                  onClick={handleClosePreview}
                  className="px-8"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* NEW: Processing Started Modal */}
      <Dialog open={showProcessingStartedModal} onOpenChange={setShowProcessingStartedModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-purple-600 flex items-center gap-2">
              <Loader2 className="h-6 w-6 animate-spin" />
              Audio Processing Started
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-gray-700 dark:text-gray-300">
              Your manuscript audio is now being processed in the background.
            </p>
            
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Mode:</span>
                <span className="font-semibold">{processingJobData?.mode === 'full' ? 'Full Production' : 'Dev Mode (75 words)'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Segments:</span>
                <span className="font-semibold">{processingJobData?.segments || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Characters:</span>
                <span className="font-semibold">{(processingJobData?.total_characters || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Estimated time:</span>
                <span className="font-semibold text-blue-600 dark:text-blue-400">
                  {processingJobData?.estimated_time_minutes || 0} minutes
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Estimated cost:</span>
                <span className="font-semibold text-blue-600 dark:text-blue-400">
                  ${(processingJobData?.estimated_cost_usd || 0).toFixed(4)}
                </span>
              </div>
            </div>
            
            <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                <strong>You can safely:</strong>
              </p>
              <ul className="text-sm text-amber-700 dark:text-amber-300 mt-2 space-y-1 ml-4 list-disc">
                <li>Close this window</li>
                <li>Log out of the application</li>
                <li>Close your browser</li>
              </ul>
              <p className="text-sm text-amber-800 dark:text-amber-200 mt-2">
                Processing will continue in the background. You&apos;ll be notified when it&apos;s complete.
              </p>
            </div>
            
            <div className="bg-slate-100 dark:bg-slate-800 p-3 rounded-lg">
              <p className="text-xs text-slate-600 dark:text-slate-400">
                While processing, the Outline Editor, Preview, and Activate buttons will be disabled.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              onClick={() => setShowProcessingStartedModal(false)}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              Got it!
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* NEW: Processing Completed Modal */}
      <Dialog open={showProcessingCompletedModal} onOpenChange={setShowProcessingCompletedModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-green-600 flex items-center gap-2">
              ✅ TTS Processing Complete!
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-gray-700 dark:text-gray-300">
              Audio processing for <strong>{completedJobData?.title}</strong> is complete and ready for preview.
            </p>
            
            {completedJobData?.progress && (
              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Segments processed:</span>
                  <span className="font-semibold text-green-600 dark:text-green-400">
                    {completedJobData.progress.segments_completed} / {completedJobData.progress.segments_total}
                  </span>
                </div>
                
                {completedJobData.costs && (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">TTS cost:</span>
                      <span className="font-semibold">
                        ${(completedJobData.costs.tts_cost_usd || 0).toFixed(4)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Total cost:</span>
                      <span className="font-semibold">
                        ${(completedJobData.costs.total_cost_usd || 0).toFixed(4)}
                      </span>
                    </div>
                  </>
                )}
              </div>
            )}
            
            {completedJobData?.progress?.segments_failed > 0 && (
              <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  ⚠️ {completedJobData.progress.segments_failed} segment(s) failed to process. 
                  You can retry them from the Outline Editor.
                </p>
              </div>
            )}
            
            <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                Your manuscript is now ready to preview and activate for readers.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              onClick={() => {
                setShowProcessingCompletedModal(false);
                loadData(); // Refresh to get latest data
              }}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              Okay
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Universal Footer */}
      <UniversalFooter />

    </div>
  );
};

export default CreatorConsole;
